const formatterCache = new Map();

const toFiniteNumber = (value, fallback = null) => {
  if (value === null || value === undefined) {
    return fallback;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toNonNegativeInteger = (value, fallback = 0) => {
  const numeric = toFiniteNumber(value, fallback);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }

  const integer = Math.trunc(numeric);
  return integer >= 0 ? integer : fallback;
};

export const toPositiveInteger = (value, fallback = 1) => {
  const integer = toNonNegativeInteger(value, fallback);
  return integer > 0 ? integer : fallback;
};

const roundCurrency = (value) => {
  const numeric = toFiniteNumber(value, 0);
  return Math.round(numeric * 100) / 100;
};

const ensureDate = (value) => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getCurrencyFormatter = (currency, locale) => {
  const key = `${locale}-${currency}`;
  if (!formatterCache.has(key)) {
    formatterCache.set(
      key,
      new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      })
    );
  }

  return formatterCache.get(key);
};

export const formatCurrency = (value, currency = "CAD", locale = "en-CA") => {
  const numeric = toFiniteNumber(value, 0);
  const formatter = getCurrencyFormatter(currency, locale);
  return formatter.format(numeric ?? 0);
};

export const formatBillingDate = (value, locale = "en-CA") => {
  const date = ensureDate(value);
  if (!date) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

export const computeNextBillingDate = (
  interval = "month",
  intervalCount = 1,
  referenceDate = new Date()
) => {
  const base = ensureDate(referenceDate) ?? new Date();
  const count = toPositiveInteger(intervalCount, 1);
  const normalizedInterval = String(interval || "month").toLowerCase();

  switch (normalizedInterval) {
    case "year":
    case "annual":
    case "annually":
      base.setFullYear(base.getFullYear() + count);
      break;
    case "week":
    case "weekly":
      base.setDate(base.getDate() + count * 7);
      break;
    case "day":
    case "daily":
      base.setDate(base.getDate() + count);
      break;
    default:
      base.setMonth(base.getMonth() + count);
      break;
  }

  return formatBillingDate(base);
};

const mapInvoice = (invoice) => {
  if (!invoice || typeof invoice !== "object") {
    return null;
  }
  return {
    id: invoice.id,
    key: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    invoiceDate: formatBillingDate(invoice.invoiceDate),
    amount: roundCurrency(invoice.amount),
    currency: invoice.currency,
    status: invoice.status,
    paidDate: formatBillingDate(invoice.paidDate),
    invoicePDFUrl: invoice?.invoicePDFUrl,
  };
};

export const mapInvoiceCollection = (payload) => {
  return payload.map((invoice) => mapInvoice(invoice)).filter(Boolean);
};

export const mapSubscriptionSummary = (payload, defaultCurrency) => {
  if (!payload) {
    return null;
  }

  const totalUsers = toNonNegativeInteger(payload.totalUsers, 0);
  const pricePerUser = toFiniteNumber(payload.pricePerUser, 0);
  const totalAmount = roundCurrency(
    payload.totalAmount ?? pricePerUser * totalUsers
  );
  const billingInterval = payload.billingInterval ?? "monthly";
  const billingIntervalCount = toPositiveInteger(
    payload.billingIntervalCount,
    1
  );
  const currency = String(payload.currency || defaultCurrency).toUpperCase();
  const nextBilling =
    formatBillingDate(payload.nextBillingDate) ??
    computeNextBillingDate(billingInterval, billingIntervalCount);

  const subscriptionId = payload.subscriptionId ?? null;
  const cancelAtPeriodEnd = Boolean(payload.cancelAtPeriodEnd);
  const cancellationEffectiveDateRaw = payload.cancellationEffectiveDate;
  const cancellationEffectiveDate = cancelAtPeriodEnd
    ? formatBillingDate(cancellationEffectiveDateRaw) ?? nextBilling
    : null;
  const canceledAtRaw = payload.canceledAt;
  const canceledAt = formatBillingDate(canceledAtRaw);
  const userCanCancel = Boolean(payload.userCanCancel);

  return {
    subscriptionId,
    planId: payload.planId,
    planName: payload.planName,
    pricePerUser,
    totalAmount,
    totalUsers,
    billingInterval,
    billingIntervalCount,
    nextBillingDate: nextBilling,
    status: payload.status,
    currency,
    userCanCancel,
    cancelAtPeriodEnd,
    cancellationEffectiveDate,
    canceledAt,
  };
};

export const mapSubscriptionResponse = (payload, defaultCurrency) => {
  if (!payload) {
    return {
      firstTime: true,
      summary: null,
      currency: defaultCurrency,
    };
  }

  if (payload.firstTime) {
    return {
      firstTime: true,
      summary: null,
      currency: defaultCurrency,
    };
  }

  const summary = mapSubscriptionSummary(payload, defaultCurrency);
  const currency = summary?.currency ?? defaultCurrency;

  return {
    firstTime: false,
    summary,
    currency,
  };
};

export const fromMinorUnits = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return null;
  }

  return numeric / 100;
};

export const normalizeCurrency = (currency, fallback = "CAD") => {
  if (!currency) {
    return fallback;
  }

  return String(currency).toUpperCase();
};

const normalizeTierBoundary = (value, fallback) => {
  if (value === null || value === undefined) {
    return fallback;
  }

  const numeric = toFiniteNumber(value, null);
  if (numeric === null) {
    return fallback;
  }

  return Math.trunc(numeric);
};

const extractTierAmount = (tier, ...fields) => {
  if (!tier) {
    return null;
  }

  for (const field of fields) {
    if (!Object.prototype.hasOwnProperty.call(tier, field)) {
      continue;
    }

    const raw = tier[field];
    const numeric = toFiniteNumber(raw, null);
    if (numeric === null) {
      continue;
    }

    return fromMinorUnits(numeric);
  }

  return null;
};

const findTierForQuantity = (tiers, quantity) => {
  if (!Array.isArray(tiers) || tiers.length === 0) {
    return null;
  }

  const normalizedQuantity = toPositiveInteger(quantity, 1);

  const matchedTier = tiers.find((tier) => {
    const start = normalizeTierBoundary(tier.start, 1) || 1;
    const endValue =
      tier.end === null || tier.end === undefined
        ? Infinity
        : normalizeTierBoundary(tier.end, Infinity);

    return (
      normalizedQuantity >= start &&
      normalizedQuantity <= (Number.isFinite(endValue) ? endValue : Infinity)
    );
  });

  return matchedTier ?? tiers[tiers.length - 1];
};

export const calculatePlanPricing = (
  plan,
  quantity,
  fallbackCurrency = "CAD"
) => {
  if (!plan) {
    return {
      totalAmount: 0,
      pricePerUser: 0,
      currency: normalizeCurrency(fallbackCurrency),
    };
  }

  const normalizedQuantity = toPositiveInteger(quantity, 1);
  const legacyPrice = toFiniteNumber(plan.pricePerUser, null);

  if (legacyPrice !== null) {
    const totalAmount = legacyPrice * normalizedQuantity;
    const currency = normalizeCurrency(plan.currency ?? fallbackCurrency);
    return {
      totalAmount,
      pricePerUser: legacyPrice,
      currency,
    };
  }

  const tier = findTierForQuantity(plan.tiers, normalizedQuantity);
  const currency = normalizeCurrency(
    plan.currency ?? tier?.currency ?? fallbackCurrency
  );

  if (!tier) {
    return {
      totalAmount: 0,
      pricePerUser: 0,
      currency,
    };
  }

  const flatAmount = extractTierAmount(tier, "flatAmountDecimal", "flatAmount");
  const unitAmount = extractTierAmount(tier, "unitAmountDecimal", "unitAmount");

  const hasFlatAmount = flatAmount !== null;
  const hasUnitAmount = unitAmount !== null;

  const flatComponent = hasFlatAmount ? flatAmount : 0;
  const unitComponent = hasUnitAmount ? unitAmount * normalizedQuantity : 0;
  const hasAnyPricing = hasFlatAmount || hasUnitAmount;

  const totalAmount = hasAnyPricing ? flatComponent + unitComponent : 0;

  const pricePerUser =
    hasAnyPricing && normalizedQuantity > 0
      ? totalAmount / normalizedQuantity
      : 0;

  return {
    totalAmount,
    pricePerUser,
    currency,
  };
};

export const getBillingCycleLabel = (cycle) => {
  const normalized = String(cycle || "month").toLowerCase();

  switch (normalized) {
    case "year":
    case "annual":
    case "annually":
      return "year";
    case "week":
    case "weekly":
      return "week";
    case "day":
    case "daily":
      return "day";
    default:
      return "month";
  }
};

export const formatBillingCommitment = (value) => {
  if (!value) {
    return null;
  }

  const normalized = String(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) {
    return null;
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const derivePlanDisplayData = (plan, quantity, options = {}) => {
  const normalizedQuantity = toPositiveInteger(quantity, 1);
  const { totalAmount, pricePerUser, currency } = calculatePlanPricing(
    plan,
    normalizedQuantity,
    options.fallbackCurrency
  );

  return {
    planId: plan.priceId,
    totalAmount,
    pricePerUser,
    currency,
    billingCycleLabel: getBillingCycleLabel(plan?.billingCycle),
    commitmentLabel: formatBillingCommitment(plan?.billingCommitment),
  };
};

export const createSubscriptionSummary = (plan, userCount, options = {}) => {
  if (!plan) {
    return null;
  }

  const normalizedUsers = toPositiveInteger(userCount, 1);
  const fallbackCurrency = options.fallbackCurrency ?? plan.currency ?? "CAD";
  const { totalAmount, pricePerUser, currency } = calculatePlanPricing(
    plan,
    normalizedUsers,
    fallbackCurrency
  );

  const resolvedPlanId = plan.id ?? plan.priceId ?? null;
  const billingInterval = plan.billingCycle ?? "month";
  const billingIntervalCount = toPositiveInteger(plan.billingIntervalCount, 1);
  const roundedTotal = roundCurrency(totalAmount);
  const roundedPricePerUser = roundCurrency(pricePerUser);

  return {
    planId: resolvedPlanId,
    planName: plan.name,
    pricePerUser: roundedPricePerUser,
    totalAmount: roundedTotal,
    totalUsers: normalizedUsers,
    billingInterval,
    billingIntervalCount,
    currency,
    nextBillingDate: computeNextBillingDate(
      billingInterval,
      billingIntervalCount
    ),
  };
};
